/**
 * CR339: Deal Type Themes Panel
 * 
 * Per-deal-type HEX color controls for Theme Labs → Colors tab.
 * Manages headerBackgroundHex and ribbonBaseHex per deal type.
 */

import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Palette } from "lucide-react";
import {
  type DealTypeThemeColors,
  DEFAULT_DEAL_TYPE_THEMES,
} from "@/lib/deals/globalThemeTokens";
import { isValidHex } from "@/lib/deals/hexColorUtils";

// ================================================================
// TYPES
// ================================================================

const DEAL_TYPES = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"] as const;

interface DealTypeThemesPanelProps {
  /** Currently selected deal type for editing */
  selectedDealType: string;
  onSelectDealType: (dt: string) => void;
  /** Current deal type themes (from draft tokens) */
  dealTypeThemes: Record<string, DealTypeThemeColors>;
  /** Called when any deal type theme color changes */
  onChange: (themes: Record<string, DealTypeThemeColors>) => void;
}

// ================================================================
// COMPONENT
// ================================================================

export function DealTypeThemesPanel({
  selectedDealType,
  onSelectDealType,
  dealTypeThemes,
  onChange,
}: DealTypeThemesPanelProps) {
  const currentTheme = useMemo(
    () => dealTypeThemes[selectedDealType] ?? DEFAULT_DEAL_TYPE_THEMES[selectedDealType] ?? DEFAULT_DEAL_TYPE_THEMES.UNCLASSIFIED,
    [dealTypeThemes, selectedDealType],
  );

  const handleColorChange = (field: keyof DealTypeThemeColors, value: string) => {
    onChange({
      ...dealTypeThemes,
      [selectedDealType]: {
        ...currentTheme,
        [field]: value,
      },
    });
  };

  const headerValid = isValidHex(currentTheme.headerBackgroundHex);
  const ribbonValid = isValidHex(currentTheme.ribbonBaseHex);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Deal Type Themes
        </CardTitle>
        <CardDescription className="text-xs">
          Per-deal-type accent colors for headers and ribbons (HEX)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Deal Type Selector */}
        <div className="space-y-1.5">
          <Label className="text-xs">Deal Type</Label>
          <Select value={selectedDealType} onValueChange={onSelectDealType}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEAL_TYPES.map((dt) => {
                const theme = dealTypeThemes[dt] ?? DEFAULT_DEAL_TYPE_THEMES[dt];
                return (
                  <SelectItem key={dt} value={dt}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: theme?.ribbonBaseHex || "#888" }}
                      />
                      {dt}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Color Controls for Selected Deal Type */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs font-mono">{selectedDealType}</Badge>
          </div>

          {/* Header Background HEX */}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-2">
              <div
                className="w-4 h-4 rounded border"
                style={{ backgroundColor: headerValid ? currentTheme.headerBackgroundHex : "#ccc" }}
              />
              Header Background
            </Label>
            <div className="flex items-center gap-2">
              <Input
                className="h-8 text-xs font-mono flex-1"
                value={currentTheme.headerBackgroundHex}
                onChange={(e) => handleColorChange("headerBackgroundHex", e.target.value)}
                placeholder="#RRGGBB"
              />
              <input
                type="color"
                value={headerValid ? currentTheme.headerBackgroundHex : "#000000"}
                onChange={(e) => handleColorChange("headerBackgroundHex", e.target.value)}
                className="w-8 h-8 rounded border cursor-pointer p-0"
              />
            </div>
            {!headerValid && currentTheme.headerBackgroundHex && (
              <p className="text-[10px] text-destructive">Invalid HEX format</p>
            )}
          </div>

          {/* Ribbon Base HEX */}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-2">
              <div
                className="w-4 h-4 rounded border"
                style={{ backgroundColor: ribbonValid ? currentTheme.ribbonBaseHex : "#ccc" }}
              />
              Ribbon Base
            </Label>
            <div className="flex items-center gap-2">
              <Input
                className="h-8 text-xs font-mono flex-1"
                value={currentTheme.ribbonBaseHex}
                onChange={(e) => handleColorChange("ribbonBaseHex", e.target.value)}
                placeholder="#RRGGBB"
              />
              <input
                type="color"
                value={ribbonValid ? currentTheme.ribbonBaseHex : "#000000"}
                onChange={(e) => handleColorChange("ribbonBaseHex", e.target.value)}
                className="w-8 h-8 rounded border cursor-pointer p-0"
              />
            </div>
            {!ribbonValid && currentTheme.ribbonBaseHex && (
              <p className="text-[10px] text-destructive">Invalid HEX format</p>
            )}
          </div>
        </div>

        {/* All Deal Types Overview */}
        <Separator />
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">All Types</Label>
          <div className="grid grid-cols-5 gap-1.5">
            {DEAL_TYPES.map((dt) => {
              const theme = dealTypeThemes[dt] ?? DEFAULT_DEAL_TYPE_THEMES[dt];
              const isActive = dt === selectedDealType;
              return (
                <button
                  key={dt}
                  onClick={() => onSelectDealType(dt)}
                  className={`flex flex-col items-center gap-1 p-1.5 rounded text-[10px] transition-colors ${
                    isActive ? "bg-accent ring-1 ring-primary" : "hover:bg-muted/50"
                  }`}
                >
                  <div
                    className="w-5 h-5 rounded-full border"
                    style={{ backgroundColor: theme?.ribbonBaseHex || "#888" }}
                  />
                  <span className="truncate w-full text-center">{dt.slice(0, 5)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
